import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import {
  InboxOutlined,
  ReloadOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import {
  applyProductionOnboardingMenu,
  applyProductionOnboardingSuggestions,
  applyProductionOnboardingTemplate,
  getProductionOnboardingPreview,
  getProductionOnboardingSuggestions,
  getProductionOnboardingTemplates,
  previewProductionOnboardingMenu,
  previewProductionOnboardingSuggestions,
  scanProductionMenu,
} from "./api";
import { getProductionTerminology } from "./productionTerminology";

const { Paragraph, Text, Title } = Typography;
const DEFAULT_TERMINOLOGY = getProductionTerminology([]);
const PRODUCTION_KIND_SEMI_FINISHED = "semi_finished";
const ELSA_COLORS = {
  brandBlue: "#295d86",
  brandBlueSoft: "#4088b8",
  brandBlueBg: "#eef5fb",
  brandBlueBorder: "#c8dced",
  brandRed: "#be4349",
  brandRedBg: "#fff1f0",
  brandRedBorder: "#f3c7cb",
  success: "#52c41a",
  successBg: "#f6ffed",
  successBorder: "#d9f7be",
  warning: "#d46b08",
  warningBg: "#fff7e6",
  warningBorder: "#ffd8a8",
  neutralBg: "#fafbfc",
  neutralBorder: "#e5e7eb",
  neutralPanel: "#f7f9fc",
  neutralText: "#667085",
};

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error?.message) {
    return error.message;
  }

  if (error?.error) {
    return error.error;
  }

  return fallback;
};

const getProductionKindLabel = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "Semi-fini" : "Fini";

const getProductionKindColor = (kind) =>
  kind === PRODUCTION_KIND_SEMI_FINISHED ? "volcano" : "geekblue";

const formatQuantity = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits,
  }).format(Number(value || 0));

const getSupportLabel = (count) =>
  `${count} reference${Number(count) > 1 ? "s" : ""}`;

const renderNameTags = (items = [], color = "default") => {
  if (!items.length) {
    return <Text type="secondary">Aucun</Text>;
  }

  return (
    <Space wrap>
      {items.slice(0, 8).map((item) => (
        <Tag key={`${item.key || item.id}-${item.name}`} color={color}>
          {item.name}
        </Tag>
      ))}
      {items.length > 8 ? <Tag>{`+${items.length - 8}`}</Tag> : null}
    </Space>
  );
};

const metricCardStyle = (background, borderColor) => ({
  padding: "10px 12px",
  borderRadius: 14,
  background,
  border: `1px solid ${borderColor}`,
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
});

const previewCardStyle = (background, borderColor) => ({
  padding: "14px 16px",
  borderRadius: 18,
  background,
  border: `1px solid ${borderColor}`,
  boxShadow: "0 8px 20px rgba(15, 23, 42, 0.04)",
});

const previewPanelStyle = {
  padding: 20,
  borderRadius: 20,
  border: `1px solid ${ELSA_COLORS.brandBlueBorder}`,
  background:
    "linear-gradient(135deg, rgba(41,93,134,0.08) 0%, rgba(255,255,255,0.98) 58%, rgba(190,67,73,0.06) 100%)",
  boxShadow: "0 14px 34px rgba(41, 93, 134, 0.08)",
};

const ProductionOnboardingModal = ({
  visible,
  warehouseId,
  warehouseName,
  companyId,
  // Profils restauration / fast-food / boulangerie : true => onglets "Packs
  // rapides" + "Importer un menu" disponibles. Sinon (production/transformation,
  // retail, e-commerce...) seules les "Recettes suggerees" sont proposees.
  packEligible = true,
  canUseMenuAi = false,
  initialMode = "suggestions",
  terminology = DEFAULT_TERMINOLOGY,
  onCancel,
  onApplied,
}) => {
  const [mode, setMode] = useState("suggestions");
  const [templates, setTemplates] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [company, setCompany] = useState(null);
  const [sourceCompanyCount, setSourceCompanyCount] = useState(0);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
  const [selectedSuggestionKeys, setSelectedSuggestionKeys] = useState([]);
  const [suggestionSearch, setSuggestionSearch] = useState("");
  const [preview, setPreview] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  // Import local standard (photo/PDF -> OCR/texte local -> matching).
  const [menuFile, setMenuFile] = useState(null);
  const [menuScanMode, setMenuScanMode] = useState("local");
  const [menuScanLoading, setMenuScanLoading] = useState(false);
  const [menuScanResult, setMenuScanResult] = useState(null);
  const [selectedMenuRefs, setSelectedMenuRefs] = useState([]); // ["source:key", ...]

  const suggestionSelectionSignature = useMemo(
    () => [...selectedSuggestionKeys].sort().join("|"),
    [selectedSuggestionKeys],
  );

  const menuSelectionSignature = useMemo(
    () => [...selectedMenuRefs].sort().join("|"),
    [selectedMenuRefs],
  );

  const parseMenuSelections = (refs) =>
    (refs || []).map((refKey) => {
      const separatorIndex = String(refKey).indexOf(":");
      return {
        source: String(refKey).slice(0, separatorIndex),
        key: String(refKey).slice(separatorIndex + 1),
      };
    });

  const filteredSuggestions = useMemo(() => {
    const normalizedSearch = String(suggestionSearch || "")
      .trim()
      .toLowerCase();

    if (!normalizedSearch) {
      return suggestions;
    }

    return suggestions.filter((item) => {
      const haystack = [
        item.label,
        item.description,
        item.production_kind,
        item.unit_short_name,
        ...(item.top_materials || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [suggestions, suggestionSearch]);

  useEffect(() => {
    const preferredMode =
      packEligible && initialMode === "templates" ? "templates" : "suggestions";

    if (!visible) {
      setMode(preferredMode);
      setTemplates([]);
      setSuggestions([]);
      setCompany(null);
      setSourceCompanyCount(0);
      setSelectedTemplateKey(null);
      setSelectedSuggestionKeys([]);
      setSuggestionSearch("");
      setPreview(null);
      setTemplatesLoading(false);
      setSuggestionsLoading(false);
      setPreviewLoading(false);
      setApplyLoading(false);
      setMenuFile(null);
      setMenuScanMode("local");
      setMenuScanLoading(false);
      setMenuScanResult(null);
      setSelectedMenuRefs([]);
      return;
    }

    if (!warehouseId) {
      setPreview(null);
      return;
    }

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const params = {};
        if (companyId) {
          params.company_id = companyId;
        }

        const data = await getProductionOnboardingTemplates(params);
        const templateList = Array.isArray(data?.templates) ? data.templates : [];
        const recommendedTemplate = templateList.find(
          (template) => template.recommended,
        );

        setCompany((current) => data?.company || current || null);
        setTemplates(templateList);
        setSelectedTemplateKey((current) => {
          if (current && templateList.some((item) => item.key === current)) {
            return current;
          }

          return recommendedTemplate?.key || templateList[0]?.key || null;
        });
      } catch (error) {
        message.error(
          getErrorMessage(
            error,
            "Erreur lors du chargement des packs d'initialisation",
          ),
        );
        setTemplates([]);
        setSelectedTemplateKey(null);
      } finally {
        setTemplatesLoading(false);
      }
    };

    const loadSuggestions = async () => {
      setSuggestionsLoading(true);
      try {
        const params = {};
        if (companyId) {
          params.company_id = companyId;
        }

        const data = await getProductionOnboardingSuggestions(params);
        const suggestionList = Array.isArray(data?.suggestions)
          ? data.suggestions
          : [];

        setCompany((current) => data?.company || current || null);
        setSourceCompanyCount(Number(data?.source_company_count) || 0);
        setSuggestions(suggestionList);
        setSelectedSuggestionKeys((current) => {
          const availableKeys = new Set(suggestionList.map((item) => item.key));
          const keptKeys = (current || []).filter((key) => availableKeys.has(key));

          return keptKeys;
        });
        setMode((current) => {
          // Profils non eligibles : jamais de bascule vers "templates" (packs
          // restauration masques), on reste sur les recettes suggerees.
          if (!packEligible) {
            return "suggestions";
          }

          if (current === "suggestions" && suggestionList.length === 0) {
            return "templates";
          }

          if (current === "templates" || current === "suggestions") {
            return current;
          }

          return suggestionList.length > 0 ? "suggestions" : "templates";
        });
      } catch (error) {
        message.error(
          getErrorMessage(error, "Erreur lors du chargement des suggestions"),
        );
        setSuggestions([]);
        setSelectedSuggestionKeys([]);
      } finally {
        setSuggestionsLoading(false);
      }
    };

    loadTemplates();
    loadSuggestions();
  }, [companyId, initialMode, visible, warehouseId, packEligible]);

  // Filet de securite : pour un profil non eligible, les onglets "templates" et
  // "menu" n'existent pas. Si le mode y derive, on revient sur "suggestions".
  useEffect(() => {
    if (!packEligible && mode !== "suggestions") {
      setMode("suggestions");
    }
  }, [packEligible, mode]);

  useEffect(() => {
    if (!visible || !warehouseId) {
      return;
    }

    const loadPreview = async () => {
      if (mode === "templates") {
        if (!selectedTemplateKey) {
          setPreview(null);
          return;
        }
      } else if (mode === "menu") {
        if (selectedMenuRefs.length === 0) {
          setPreview(null);
          return;
        }
      } else if (selectedSuggestionKeys.length === 0) {
        setPreview(null);
        return;
      }

      setPreviewLoading(true);
      try {
        if (mode === "templates") {
          const params = { warehouse_id: warehouseId };
          if (companyId) {
            params.company_id = companyId;
          }

          const data = await getProductionOnboardingPreview(
            selectedTemplateKey,
            params,
          );
          setPreview(data);
        } else if (mode === "menu") {
          const payload = {
            warehouse_id: warehouseId,
            selections: parseMenuSelections(selectedMenuRefs),
          };

          if (companyId) {
            payload.company_id = companyId;
          }

          const data = await previewProductionOnboardingMenu(payload);
          setPreview(data);
        } else {
          const payload = {
            warehouse_id: warehouseId,
            suggestion_keys: selectedSuggestionKeys,
          };

          if (companyId) {
            payload.company_id = companyId;
          }

          const data = await previewProductionOnboardingSuggestions(payload);
          setPreview(data);
        }
      } catch (error) {
        setPreview(null);
        message.error(
          getErrorMessage(
            error,
            mode === "templates"
              ? "Erreur lors de la previsualisation du pack"
              : mode === "menu"
                ? `Erreur lors de la prévisualisation des ${terminology.configurationPlural} du menu`
                : "Erreur lors de la previsualisation des suggestions",
          ),
        );
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [
    visible,
    mode,
    selectedTemplateKey,
    suggestionSelectionSignature,
    menuSelectionSignature,
    warehouseId,
    companyId,
    selectedSuggestionKeys,
    selectedMenuRefs,
    terminology.configurationPlural,
  ]);

  const handleApply = async () => {
    if (!warehouseId) {
      return;
    }

    if (preview && preview.can_apply === false) {
      message.error(
        mode === "templates"
          ? "Le pack contient des conflits et ne peut pas etre applique."
          : "La selection contient des conflits et ne peut pas etre appliquee.",
      );
      return;
    }

    setApplyLoading(true);
    try {
      let result;

      if (mode === "templates") {
        const payload = {
          template_key: selectedTemplateKey,
          warehouse_id: warehouseId,
        };

        if (companyId) {
          payload.company_id = companyId;
        }

        result = await applyProductionOnboardingTemplate(payload);
      } else if (mode === "menu") {
        const payload = {
          warehouse_id: warehouseId,
          selections: parseMenuSelections(selectedMenuRefs),
        };

        if (companyId) {
          payload.company_id = companyId;
        }

        result = await applyProductionOnboardingMenu(payload);
      } else {
        const payload = {
          warehouse_id: warehouseId,
          suggestion_keys: selectedSuggestionKeys,
        };

        if (companyId) {
          payload.company_id = companyId;
        }

        result = await applyProductionOnboardingSuggestions(payload);
      }

      message.success(
        result?.message || "Initialisation de production effectuee avec succes",
      );

      if (onApplied) {
        onApplied(result);
      }
    } catch (error) {
      if (error?.preview) {
        setPreview(error.preview);
      }

      message.error(
        getErrorMessage(
          error,
          mode === "templates"
            ? "Erreur lors de l'initialisation du pack"
            : "Erreur lors de l'initialisation de la selection",
        ),
      );
    } finally {
      setApplyLoading(false);
    }
  };

  const toggleSuggestion = (suggestionKey) => {
    setSelectedSuggestionKeys((current) => {
      if (current.includes(suggestionKey)) {
        return current.filter((key) => key !== suggestionKey);
      }

      return [...current, suggestionKey];
    });
  };

  const toggleMenuRef = (refKey) => {
    setSelectedMenuRefs((current) =>
      current.includes(refKey)
        ? current.filter((key) => key !== refKey)
        : [...current, refKey],
    );
  };

  const handleScanMenu = async () => {
    if (!warehouseId || !menuFile) {
      return;
    }

    setMenuScanLoading(true);
    try {
      const formData = new FormData();
      formData.append("menu", menuFile);

      const params = { warehouse_id: warehouseId };
      params.mode = menuScanMode;
      if (companyId) {
        params.company_id = companyId;
      }

      const data = await scanProductionMenu(formData, params);
      setMenuScanResult(data);
      setSelectedMenuRefs([]);
      setPreview(null);

      if (data?.eligible === false) {
        message.info(
          data.reason === "menu_scan_ai_disabled"
            ? "L'analyse IA n'est pas activee sur cette installation."
            : "Cette fonctionnalite est reservee aux profils restauration / boulangerie / fast-food.",
        );
      } else if ((data?.matched || []).length === 0) {
        message.info(
          `Aucune ${terminology.unitSingular} initialisable n'a été détectée sur cette carte.`,
        );
      }
    } catch (error) {
      setMenuScanResult(null);
      message.error(
        getErrorMessage(error, "Erreur lors de l'analyse du menu importe"),
      );
    } finally {
      setMenuScanLoading(false);
    }
  };

  const renderSuggestionItem = (item) => {
    const checked = selectedSuggestionKeys.includes(item.key);
    const supportCount = Number(item.support_count) || 0;
    const outputQuantity = Number(item.output_quantity) || 1;

    return (
      <List.Item style={{ padding: 0, border: "none", marginBottom: 12 }}>
        <div
          style={{
            width: "100%",
            padding: "18px 20px",
            borderRadius: 16,
            border: checked
              ? `1px solid ${ELSA_COLORS.brandBlueBorder}`
              : `1px solid ${ELSA_COLORS.neutralBorder}`,
            background: checked
              ? "linear-gradient(135deg, rgba(41,93,134,0.08) 0%, rgba(255,255,255,1) 62%, rgba(190,67,73,0.05) 100%)"
              : "#ffffff",
            boxShadow: checked
              ? "0 12px 28px rgba(41, 93, 134, 0.12)"
              : "0 6px 18px rgba(15, 23, 42, 0.06)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 14,
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Checkbox
              checked={checked}
              onChange={() => toggleSuggestion(item.key)}
              style={{ marginTop: 4 }}
            />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: 260 }}>
                  <Space wrap size={[8, 8]} style={{ marginBottom: 6 }}>
                    <Text strong style={{ fontSize: 17 }}>
                      {item.label}
                    </Text>
                    <Tag
                      style={{
                        color: checked
                          ? ELSA_COLORS.brandBlue
                          : ELSA_COLORS.neutralText,
                        background: checked
                          ? ELSA_COLORS.brandBlueBg
                          : "#f5f5f5",
                        borderColor: checked
                          ? ELSA_COLORS.brandBlueBorder
                          : "#e5e7eb",
                        borderRadius: 999,
                        paddingInline: 10,
                      }}
                    >
                      {checked ? "Selectionnee" : "Disponible"}
                    </Tag>
                    <Tag color={getProductionKindColor(item.production_kind)}>
                      {getProductionKindLabel(item.production_kind)}
                    </Tag>
                    {item.unit_short_name ? (
                      <Tag
                        style={{
                          color: ELSA_COLORS.brandRed,
                          background: ELSA_COLORS.brandRedBg,
                          borderColor: ELSA_COLORS.brandRedBorder,
                          borderRadius: 999,
                          paddingInline: 10,
                        }}
                      >
                        {item.unit_short_name}
                      </Tag>
                    ) : null}
                  </Space>

                  <Paragraph
                    type="secondary"
                    style={{ marginBottom: 0 }}
                    ellipsis={{ rows: 2 }}
                  >
                    {item.description ||
                      `${terminology.configurationSingularTitle} suggérée pour cette activité.`}
                  </Paragraph>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
                    gap: 8,
                    minWidth: 260,
                    flex: "0 1 360px",
                  }}
                >
                  <div
                    style={metricCardStyle(
                      ELSA_COLORS.brandBlueBg,
                      ELSA_COLORS.brandBlueBorder,
                    )}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      References
                    </Text>
                    <div>
                      <Text strong style={{ color: ELSA_COLORS.brandBlue }}>
                        {getSupportLabel(supportCount)}
                      </Text>
                    </div>
                  </div>
                  <div
                    style={metricCardStyle(
                      ELSA_COLORS.brandRedBg,
                      ELSA_COLORS.brandRedBorder,
                    )}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Sortie type
                    </Text>
                    <div>
                      <Text strong style={{ color: ELSA_COLORS.brandRed }}>
                        {`${formatQuantity(outputQuantity)} ${
                          item.unit_short_name || "u"
                        }`}
                      </Text>
                    </div>
                  </div>
                  <div
                    style={metricCardStyle(
                      ELSA_COLORS.neutralBg,
                      ELSA_COLORS.neutralBorder,
                    )}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {terminology.materialPluralTitle}
                    </Text>
                    <div>
                      <Text strong style={{ color: ELSA_COLORS.brandBlueSoft }}>
                        {`${item.counts?.materials || 0} element(s)`}
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <Text
                  type="secondary"
                  style={{ display: "block", marginBottom: 8, fontSize: 12 }}
                >
                  Aperçu des {terminology.materialPlural}
                </Text>
                <Space wrap size={[8, 8]}>
                  {(item.top_materials || []).map((materialName) => (
                    <Tag
                      key={`${item.key}-${materialName}`}
                      style={{
                        borderRadius: 999,
                        paddingInline: 10,
                        background: ELSA_COLORS.neutralPanel,
                        borderColor: ELSA_COLORS.brandBlueBorder,
                        color: ELSA_COLORS.brandBlue,
                      }}
                    >
                      {materialName}
                    </Tag>
                  ))}
                  {item.counts?.variable_costs ? (
                    <Tag
                      style={{
                        color: ELSA_COLORS.brandRed,
                        background: ELSA_COLORS.brandRedBg,
                        borderColor: ELSA_COLORS.brandRedBorder,
                        borderRadius: 999,
                        paddingInline: 10,
                      }}
                    >
                      {`${item.counts.variable_costs} cout(s) variable(s)`}
                    </Tag>
                  ) : null}
                </Space>
              </div>
            </div>
          </div>
        </div>
      </List.Item>
    );
  };

  const renderPreviewMetricCard = (
    label,
    value,
    background,
    borderColor,
    valueColor,
  ) => (
    <div style={previewCardStyle(background, borderColor)}>
      <Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
        {label}
      </Text>
      <Text strong style={{ fontSize: 22, color: valueColor }}>
        {formatQuantity(value, 0)}
      </Text>
    </div>
  );

  const renderPreviewGroupCard = (
    title,
    subtitle,
    items,
    color,
    background,
    borderColor,
  ) => (
    <div
      style={{
        ...previewCardStyle(background, borderColor),
        minHeight: 150,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <Space wrap size={[8, 8]}>
          <Text strong>{title}</Text>
          <Tag color={color}>{`${items?.length || 0} element(s)`}</Tag>
        </Space>
        {subtitle ? (
          <Text
            type="secondary"
            style={{ display: "block", marginTop: 6, fontSize: 12 }}
          >
            {subtitle}
          </Text>
        ) : null}
      </div>
      <div style={{ marginTop: "auto" }}>{renderNameTags(items, color)}</div>
    </div>
  );

  const conflictItems = [
    ...(preview?.products?.blocking_conflicts || []),
    ...(preview?.production_units?.blocking_conflicts || []),
  ];

  // Transforme un conflit bloquant en message lisible : nom precis du produit,
  // detail du conflit (unite actuelle vs unite attendue) et consigne de resolution.
  const formatConflict = (item = {}) => {
    const productName = item.name ? `« ${item.name} »` : "ce produit";
    const existingUnit = item.existing_unit_short_name || "non definie";
    const expectedUnit = item.expected_unit_short_name || "non definie";

    switch (item.reason) {
      case "unit_mismatch":
        return {
          title: `Conflit d'unite — ${productName}`,
          detail: `Un produit actif nommé ${productName} existe déjà avec l'unité « ${existingUnit} », mais la ${terminology.configurationSingular} attend « ${expectedUnit} ».`,
          hint: `Pour lever le blocage : ouvrez la fiche du produit dans Gestion des produits et alignez son unité sur « ${expectedUnit} », ou retirez cette ${terminology.configurationSingular} de la sélection pour initialiser le reste.`,
        };
      case "product_type_mismatch":
        return {
          title: `Conflit de type — ${productName}`,
          detail: `Un produit actif nomme ${productName} existe deja avec un type different${
            item.existing_product_type ? ` (${item.existing_product_type})` : ""
          }.`,
          hint: `Pour lever le blocage : renommez la ${terminology.configurationSingular} ou le produit attendu, ou retirez cette ${terminology.configurationSingular} de la sélection.`,
        };
      case "deleted_product_exists":
      case "deleted_production_unit_exists":
        return {
          title: `Element supprime existant — ${productName}`,
          detail:
            item.message ||
            `Un element supprime nomme ${productName} existe deja dans cet établissement.`,
          hint: "Restaurez ou supprimez definitivement cet element dans Gestion des produits avant de reinitialiser.",
        };
      default:
        return {
          title: item.name ? productName : "Conflit",
          detail: item.message || "Conflit bloquant detecte.",
          hint: "",
        };
    }
  };

  const renderMenuPanel = () => {
    const matched = menuScanResult?.matched || [];
    const alreadyExisting = menuScanResult?.already_existing || [];
    const missing = menuScanResult?.missing || [];
    const extracted = menuScanResult?.extracted_dishes || [];
    const notEligible = menuScanResult && menuScanResult.eligible === false;
    const extractionConfidence = Number(menuScanResult?.source?.confidence);
    const hasExtractionConfidence = Number.isFinite(extractionConfidence);
    const lowExtractionConfidence =
      hasExtractionConfidence &&
      menuScanResult?.extraction_mode === "local" &&
      extractionConfidence < 0.7;

    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message="Importer une carte / un menu"
          description="Deposez une photo ou un PDF de votre carte. Le systeme detecte les plats et propose d'initialiser ceux qui ont une correspondance dans le systeme."
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <Text strong>Mode d'import</Text>
          <Select
            value={menuScanMode}
            onChange={(value) => {
              setMenuScanMode(value);
              setMenuScanResult(null);
              setSelectedMenuRefs([]);
              setPreview(null);
            }}
            style={{ minWidth: 280 }}
            options={[
              {
                value: "local",
                label: "Standard — analyse locale (sans IA)",
              },
              ...(canUseMenuAi
                ? [
                    {
                      value: "ai",
                      label: "Avancé — analyse IA (permission requise)",
                    },
                  ]
                : []),
            ]}
          />
          {!canUseMenuAi ? (
            <Text type="secondary">
              Le mode standard est disponible. Le mode IA nécessite une permission dédiée.
            </Text>
          ) : null}
        </div>

        <Upload.Dragger
          accept="image/*,.pdf,application/pdf"
          multiple={false}
          maxCount={1}
          beforeUpload={(file) => {
            setMenuFile(file);
            setMenuScanResult(null);
            setSelectedMenuRefs([]);
            setPreview(null);
            return false;
          }}
          onRemove={() => {
            setMenuFile(null);
            setMenuScanResult(null);
            setSelectedMenuRefs([]);
            setPreview(null);
          }}
          fileList={
            menuFile
              ? [{ uid: "menu-file", name: menuFile.name, status: "done" }]
              : []
          }
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Cliquez ou glissez une photo / un PDF de la carte
          </p>
          <p className="ant-upload-hint">
            Formats acceptes : image (JPG, PNG, WebP) ou PDF — 10 Mo maximum.
          </p>
        </Upload.Dragger>

        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Button
            type="primary"
            icon={<RocketOutlined />}
            loading={menuScanLoading}
            disabled={!menuFile}
            onClick={handleScanMenu}
          >
            Analyser la carte
          </Button>
          {extracted.length > 0 ? (
            <Tag color="processing">{`${extracted.length} plat(s) detecte(s)`}</Tag>
          ) : null}
          {menuScanResult?.source_company_count ? (
            <Tag>{`${menuScanResult.source_company_count} reference(s) prises en compte`}</Tag>
          ) : null}
          {menuScanResult?.extraction_mode ? (
            <Tag color={menuScanResult.extraction_mode === "ai" ? "purple" : "green"}>
              {menuScanResult.extraction_mode === "ai"
                ? "Analyse IA"
                : "Analyse locale"}
            </Tag>
          ) : null}
          {hasExtractionConfidence && menuScanResult?.extraction_mode === "local" ? (
            <Tag color={lowExtractionConfidence ? "orange" : "blue"}>
              {`Confiance OCR : ${Math.round(extractionConfidence * 100)} %`}
            </Tag>
          ) : null}
        </div>

        {lowExtractionConfidence ? (
          <Alert
            type="warning"
            showIcon
            message="Lecture locale peu fiable"
            description="Vérifiez attentivement les plats détectés avant de les sélectionner. Une image plus nette ou un PDF texte donnera de meilleurs résultats."
          />
        ) : null}

        {notEligible ? (
          <Alert
            type="warning"
            showIcon
            message={
              menuScanResult.reason === "menu_scan_ai_disabled"
                ? "L'analyse IA n'est pas activee sur cette installation."
                : "Cette fonctionnalite est reservee aux profils restauration / boulangerie / fast-food."
            }
          />
        ) : menuScanResult ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 8,
                }}
              >
                <Text strong>{`Correspondances trouvees (${matched.length})`}</Text>
                {matched.length > 0 ? (
                  <Space wrap>
                    <Button
                      size="small"
                      onClick={() =>
                        setSelectedMenuRefs(
                          matched.map((item) => `${item.source}:${item.key}`),
                        )
                      }
                    >
                      Tout selectionner
                    </Button>
                    <Button size="small" onClick={() => setSelectedMenuRefs([])}>
                      Vider
                    </Button>
                    <Tag color="processing">
                      {`${selectedMenuRefs.length} selectionnee(s)`}
                    </Tag>
                  </Space>
                ) : null}
              </div>

              {matched.length === 0 ? (
                <Empty
                  description={`Aucune ${terminology.unitSingular} initialisable détectée sur cette carte`}
                />
              ) : (
                <List
                  split={false}
                  dataSource={matched}
                  renderItem={(item) => {
                    const refKey = `${item.source}:${item.key}`;
                    const checked = selectedMenuRefs.includes(refKey);

                    return (
                      <List.Item style={{ padding: "8px 0" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            width: "100%",
                            alignItems: "flex-start",
                          }}
                        >
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleMenuRef(refKey)}
                          />
                          <div style={{ flex: 1 }}>
                            <Space wrap>
                              <Text strong>{item.dish_name}</Text>
                              {item.matched_label &&
                              item.matched_label !== item.dish_name ? (
                                <Tag>{`≈ ${item.matched_label}`}</Tag>
                              ) : null}
                              <Tag
                                color={
                                  item.source === "suggestion"
                                    ? "geekblue"
                                    : "purple"
                                }
                              >
                                {item.source === "suggestion"
                                  ? `${terminology.configurationSingularTitle} d'activité`
                                  : "Catalogue"}
                              </Tag>
                              <Tag color="green">
                                {`${Math.round(
                                  (Number(item.confidence) || 0) * 100,
                                )}%`}
                              </Tag>
                            </Space>
                            {(item.top_materials || []).length > 0 ? (
                              <div style={{ marginTop: 4 }}>
                                <Space wrap>
                                  {item.top_materials.map((name) => (
                                    <Tag key={name}>{name}</Tag>
                                  ))}
                                </Space>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              )}
            </div>

            {alreadyExisting.length > 0 ? (
              <div>
                <Text strong>{`Deja presentes (${alreadyExisting.length})`}</Text>
                <List
                  split={false}
                  dataSource={alreadyExisting}
                  renderItem={(item) => (
                    <List.Item style={{ padding: "6px 0" }}>
                      <Space wrap>
                        <Text>{item.dish_name}</Text>
                        {item.recipe_name &&
                        item.recipe_name !== item.dish_name ? (
                          <Tag>{`≈ ${item.recipe_name}`}</Tag>
                        ) : null}
                        <Tag color="default">Deja creee</Tag>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            ) : null}

            {missing.length > 0 ? (
              <div>
                <Text strong>{`Sans correspondance (${missing.length})`}</Text>
                <Paragraph type="secondary" style={{ margin: "8px 0" }}>
                  Ces plats n'ont pas de correspondance dans le systeme. Vous
                  pourrez les créer manuellement depuis « {terminology.unitsTitle} ».
                </Paragraph>
                <Space wrap>
                  {missing.map((item) => (
                    <Tag key={item.dish_name} color="orange">
                      {item.dish_name}
                    </Tag>
                  ))}
                </Space>
              </div>
            ) : null}
          </Space>
        ) : null}
      </Space>
    );
  };

  const canApply =
    mode === "templates"
      ? Boolean(selectedTemplateKey)
      : mode === "menu"
        ? selectedMenuRefs.length > 0
        : selectedSuggestionKeys.length > 0;

  const tabItems = [
    {
      key: "suggestions",
      label: `${terminology.configurationPluralTitle} suggérées (${suggestions.length})`,
      children: suggestionsLoading ? (
        <div style={{ padding: "24px 0", textAlign: "center" }}>
          <Spin />
        </div>
      ) : suggestions.length === 0 ? (
        <Empty
          description={`Aucune ${terminology.configurationSingular} suggérée disponible pour ce profil`}
        />
      ) : (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Suggestions pour votre activite"
            description={`Sélectionnez les ${terminology.configurationPlural} à initialiser. ${sourceCompanyCount} référence(s) ont été prises en compte pour préparer cette sélection.`}
          />

          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Input.Search
              allowClear
              placeholder={`Rechercher une ${terminology.configurationSingular} ou ${terminology.materialIndefiniteSingular}`}
              value={suggestionSearch}
              onChange={(event) => setSuggestionSearch(event.target.value)}
              style={{ width: 320, maxWidth: "100%" }}
            />

            <Space wrap>
              <Button
                size="small"
                onClick={() =>
                  setSelectedSuggestionKeys(
                    filteredSuggestions.map((item) => item.key),
                  )
                }
              >
                Tout selectionner
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedSuggestionKeys([])}
              >
                Vider
              </Button>
              <Tag color="processing">
                {`${selectedSuggestionKeys.length} selectionnee(s)`}
              </Tag>
              {suggestionSearch ? (
                <Tag>{`${filteredSuggestions.length} resultat(s)`}</Tag>
              ) : null}
            </Space>
          </div>

          <List
            split={false}
            dataSource={filteredSuggestions}
            locale={{
              emptyText: (
                <Empty
                  description={`Aucune ${terminology.configurationSingular} ne correspond à votre recherche`}
                />
              ),
            }}
            renderItem={renderSuggestionItem}
          />
        </Space>
      ),
    },
    {
      key: "templates",
      label: `Packs rapides (${templates.length})`,
      children: templatesLoading ? (
        <div style={{ padding: "24px 0", textAlign: "center" }}>
          <Spin />
        </div>
      ) : templates.length === 0 ? (
        <Empty description="Aucun pack disponible pour cette entreprise" />
      ) : (
        <div>
          <Text strong>Pack</Text>
          <Select
            style={{ width: "100%", marginTop: 8 }}
            value={selectedTemplateKey}
            onChange={setSelectedTemplateKey}
            options={templates.map((template) => ({
              value: template.key,
              label: `${template.label}${
                template.recommended ? " - recommande" : ""
              }`,
            }))}
          />
        </div>
      ),
    },
    {
      key: "menu",
      label: "Importer un menu",
      children: renderMenuPanel(),
    },
  ].filter((tab) => packEligible || tab.key === "suggestions");

  return (
    <Modal
      open={visible}
      title="Initialiser la production"
      onCancel={onCancel}
      onOk={handleApply}
      okText={
        mode === "templates"
          ? "Appliquer le pack"
          : mode === "menu"
            ? `Initialiser les ${terminology.configurationPlural}`
            : "Initialiser la selection"
      }
      cancelText="Fermer"
      okButtonProps={{
        loading: applyLoading,
        disabled:
          !warehouseId ||
          templatesLoading ||
          suggestionsLoading ||
          previewLoading ||
          !canApply ||
          !preview ||
          preview.can_apply === false,
      }}
      width={1000}
      destroyOnClose
    >
      {!warehouseId ? (
        <Alert
          type="info"
          showIcon
          message={`Sélectionnez d'abord un établissement pour initialiser des ${terminology.configurationPlural}.`}
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              {
                key: "company",
                label: "Entreprise",
                children: company?.name || preview?.company?.name || "-",
              },
              {
                key: "domain",
                label: "Domaine",
                children:
                  company?.business_domain ||
                  preview?.company?.business_domain ||
                  "-",
              },
              {
                key: "profile",
                label: "Profil",
                children:
                  company?.activity_profile_label ||
                  preview?.company?.activity_profile_label ||
                  "Aucun profil renseigne",
              },
              {
                key: "warehouse",
                label: "Établissement",
                children: preview?.warehouse?.name || warehouseName || warehouseId,
              },
            ]}
          />

          <Tabs activeKey={mode} onChange={setMode} items={tabItems} />

          {previewLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <Spin />
            </div>
          ) : preview ? (
            <div style={previewPanelStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <div style={{ flex: "1 1 340px" }}>
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      marginBottom: 6,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                    }}
                  >
                    Previsualisation
                  </Text>
                  <Title level={5} style={{ marginBottom: 4 }}>
                    {preview.template?.label}
                  </Title>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    {preview.template?.description}
                  </Paragraph>
                </div>

                <Space wrap size={[8, 8]}>
                  <Tag
                    style={{
                      color: preview.template?.recommended
                        ? ELSA_COLORS.brandBlue
                        : ELSA_COLORS.brandRed,
                      background: preview.template?.recommended
                        ? ELSA_COLORS.brandBlueBg
                        : ELSA_COLORS.brandRedBg,
                      borderColor: preview.template?.recommended
                        ? ELSA_COLORS.brandBlueBorder
                        : ELSA_COLORS.brandRedBorder,
                      borderRadius: 999,
                      paddingInline: 10,
                    }}
                  >
                    {preview.template?.recommended
                      ? "Recommande pour votre profil"
                      : "Compatible avec votre activite"}
                  </Tag>
                  <Tag
                    style={{
                      color:
                        preview.can_apply === false
                          ? ELSA_COLORS.warning
                          : ELSA_COLORS.brandBlue,
                      background:
                        preview.can_apply === false
                          ? ELSA_COLORS.warningBg
                          : ELSA_COLORS.brandBlueBg,
                      borderColor:
                        preview.can_apply === false
                          ? ELSA_COLORS.warningBorder
                          : ELSA_COLORS.brandBlueBorder,
                      borderRadius: 999,
                      paddingInline: 10,
                    }}
                  >
                    {preview.can_apply === false
                      ? "Action requise"
                      : "Pret a initialiser"}
                  </Tag>
                </Space>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 10,
                  marginBottom: 14,
                }}
              >
                {renderPreviewMetricCard(
                  "Categories a creer",
                  preview.summary?.categories_to_create || 0,
                  ELSA_COLORS.brandBlueBg,
                  ELSA_COLORS.brandBlueBorder,
                  ELSA_COLORS.brandBlue,
                )}
                {renderPreviewMetricCard(
                  "Unites a creer",
                  preview.summary?.units_to_create || 0,
                  "#f5f9fd",
                  "#d9e8f5",
                  ELSA_COLORS.brandBlueSoft,
                )}
                {renderPreviewMetricCard(
                  "Produits a creer",
                  preview.summary?.products_to_create || 0,
                  ELSA_COLORS.neutralBg,
                  ELSA_COLORS.neutralBorder,
                  ELSA_COLORS.brandBlueSoft,
                )}
                {renderPreviewMetricCard(
                  `${terminology.unitsTitle} à créer`,
                  preview.summary?.production_units_to_create || 0,
                  ELSA_COLORS.brandRedBg,
                  ELSA_COLORS.brandRedBorder,
                  ELSA_COLORS.brandRed,
                )}
                {renderPreviewMetricCard(
                  "Conflits",
                  preview.summary?.blocking_conflicts || 0,
                  ELSA_COLORS.warningBg,
                  ELSA_COLORS.warningBorder,
                  ELSA_COLORS.warning,
                )}
              </div>

              <div style={{ marginBottom: 14 }}>
                {conflictItems.length > 0 ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={`${conflictItems.length} conflit(s) bloquent cette initialisation. Corrigez-les ou retirez les ${terminology.configurationPlural} concernées.`}
                    description={
                      <List
                        size="small"
                        dataSource={conflictItems}
                        renderItem={(item) => {
                          const conflict = formatConflict(item);
                          return (
                            <List.Item>
                              <Space
                                direction="vertical"
                                size={2}
                                style={{ width: "100%" }}
                              >
                                <Text strong>{conflict.title}</Text>
                                <Text>{conflict.detail}</Text>
                                {conflict.hint ? (
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {conflict.hint}
                                  </Text>
                                ) : null}
                              </Space>
                            </List.Item>
                          );
                        }}
                      />
                    }
                  />
                ) : (
                  <Alert
                    type="success"
                    showIcon
                    message="Aucun conflit bloquant detecte."
                  />
                )}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    border: `1px solid ${ELSA_COLORS.brandBlueBorder}`,
                    background:
                      "linear-gradient(180deg, rgba(238,245,251,0.95) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Ce qui sera ajoute
                    </Text>
                    <Text type="secondary">
                      Nouveaux elements crees dans cet établissement.
                    </Text>
                  </div>
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%" }}
                  >
                    {renderPreviewGroupCard(
                      "Produits a creer",
                      terminology.newMaterialsOrArticlesDescription,
                      preview.products?.create,
                      "geekblue",
                      ELSA_COLORS.brandBlueBg,
                      ELSA_COLORS.brandBlueBorder,
                    )}
                    {renderPreviewGroupCard(
                      `${terminology.unitsTitle} à créer`,
                      `Nouvelles ${terminology.configurationPlural} qui seront initialisées.`,
                      preview.production_units?.create,
                      "volcano",
                      ELSA_COLORS.brandRedBg,
                      ELSA_COLORS.brandRedBorder,
                    )}
                  </Space>
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    border: `1px solid ${ELSA_COLORS.neutralBorder}`,
                    background:
                      "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,1) 100%)",
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: "block", marginBottom: 4 }}>
                      Ce qui sera reutilise
                    </Text>
                    <Text type="secondary">
                      Elements deja disponibles et reconnus automatiquement.
                    </Text>
                  </div>
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: "100%" }}
                  >
                    {renderPreviewGroupCard(
                      "Produits reconnus",
                      "Produits deja presents et reutilisables immediatement.",
                      preview.products?.reuse,
                      "geekblue",
                      ELSA_COLORS.neutralBg,
                      ELSA_COLORS.neutralBorder,
                    )}
                    {renderPreviewGroupCard(
                      `${terminology.unitsTitle} reconnues`,
                      `${terminology.configurationPluralTitle} déjà détectées dans cet établissement.`,
                      preview.production_units?.reuse,
                      "default",
                      "#fffaf7",
                      "#f2ddd3",
                    )}
                  </Space>
                </div>
              </div>

              <Space wrap size={[8, 8]} style={{ marginTop: 14 }}>
                <Tag icon={<RocketOutlined />} color="processing">
                  Import modifiable apres creation
                </Tag>
                <Tag icon={<ReloadOutlined />}>Ajout sans ecrasement</Tag>
              </Space>
            </div>
          ) : (
            <Empty
              description={`Sélectionnez un pack ou des ${terminology.configurationPlural} pour afficher une prévisualisation`}
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default ProductionOnboardingModal;
